import React from 'react';
import { Edit, Trash2, Building2, Settings, Banknote, Package, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Client } from '@/types/client';

interface ClientsTableProps {
  clients: Client[];
  onEditClient: (client: Client) => void;
  onDeleteClient: (client: Client) => void;
  onBusinessRules: (client: Client) => void;
  onProductsClick?: (client: Client) => void;
  onFinancialClick?: (client: Client) => void;
  onContractClick?: (client: Client) => void;
  generatingContractClientId?: number | null;
}

const ClientsTable: React.FC<ClientsTableProps> = ({ 
  clients, 
  onEditClient, 
  onDeleteClient, 
  onBusinessRules,
  onProductsClick,
  onFinancialClick,
  onContractClick,
  generatingContractClientId = null,
}) => {
  const formatDate = (dateString: string) => {
    console.log('Data original (ClientsTable):', dateString);
    const date = new Date(dateString);
    console.log('Objeto Date (ClientsTable):', date);
    if (isNaN(date.getTime())) {
      return 'Data Inválida';
    }
    return date.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  if (clients.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-8 h-8 text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-montserrat font-semibold text-gray-900 mb-2">
              Nenhum cliente encontrado
            </h3>
            <p className="text-gray-600">
              Não há clientes cadastrados ou que correspondam aos critérios de busca.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="text-gray-700 font-montserrat font-semibold">ID</TableHead>
            <TableHead className="text-gray-700 font-montserrat font-semibold">Nome Fantasia</TableHead>
            <TableHead className="text-gray-700 font-montserrat font-semibold">Status</TableHead>
            <TableHead className="text-gray-700 font-montserrat font-semibold">Cadastro</TableHead>
            <TableHead className="text-gray-700 font-montserrat font-semibold">Regra de negócio</TableHead>
            <TableHead className="text-gray-700 font-montserrat font-semibold">Produtos e observações</TableHead>
            <TableHead className="text-gray-700 font-montserrat font-semibold">Financeiro</TableHead>
            <TableHead className="text-gray-700 font-montserrat font-semibold text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((client) => (
            <TableRow 
              key={client.id} 
              className="hover:bg-gray-50 transition-colors border-b border-gray-100"
            >
              <TableCell className="font-mono text-sm text-gray-900 font-normal">
                {client.id}
              </TableCell>
              <TableCell className="font-medium text-gray-900">
                {client.trade_name || client.legal_name || '-'}
              </TableCell>
              <TableCell>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  client.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {client.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </TableCell>
              <TableCell className="text-gray-700 text-sm">
                {formatDate(client.created_at)}
              </TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBusinessRules(client)}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  title="Regra de negócio"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onProductsClick?.(client)}
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  title="Produtos e observações"
                >
                  <Package className="w-4 h-4" />
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onFinancialClick?.(client)}
                  disabled={!client.asaas_id}
                  title={client.asaas_id ? 'Cobranças e informações financeiras' : 'Cadastre o ID Asaas do cliente nos dados do cliente'}
                  className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                >
                  <Banknote className="w-4 h-4" />
                </Button>
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEditClient(client)}
                    className="text-primary-600 border-primary-200 hover:bg-primary-50"
                    title="Editar cliente"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onContractClick?.(client)}
                    disabled={generatingContractClientId === client.id}
                    className="text-slate-600 border-slate-200 hover:bg-slate-50"
                    title="Emitir contrato"
                  >
                    {generatingContractClientId === client.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDeleteClient(client)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ClientsTable;
